import type { SidebarItem, SidebarSection } from '../shell';
import type { EntityPermissions, PermissionAction } from './usePermissions';

/**
 * Nav permission gating — the pure decision layer behind the operator sidebar.
 *
 * Every browsable object nav item declares a `gate`; App.tsx filters the sections
 * through {@link visibleSections} before handing them to the Sidebar. Kept as a
 * pure function (no hooks) so the gating rules are unit-testable in isolation.
 *
 * Default-DENY: while the permission grid is still loading (`perms` undefined) an
 * entity-gated item is hidden, so a control never renders speculatively. This
 * mirrors useEntityPermission() and the ENGINEERING_STANDARDS "the UI renders the
 * SoD model, never softens it" convention.
 */

export interface NavGate {
  /** Governed entity whose `can_view` (or `action`) grant reveals this item. */
  entity?: string;
  /** Permission action to check on `entity` (default 'can_view'). */
  action?: PermissionAction;
  /** Item is visible to super_admin only, regardless of any grant. */
  superAdmin?: boolean;
}

export interface GatedItem extends SidebarItem {
  gate?: NavGate;
}

export interface GatedSection {
  label?: string;
  items: GatedItem[];
}

type PermMap = Record<string, EntityPermissions> | undefined;

/**
 * Is a single nav item visible for this user's permissions + role?
 *
 * `isLoading` distinguishes "the permission grid hasn't arrived yet" from "it
 * arrived and this entity isn't granted". While loading we show gated items
 * OPTIMISTICALLY so the nav item set is stable from first paint — otherwise a
 * gated item is hidden during hydration and then pops in when the grid resolves,
 * shifting every item below it under the cursor and dropping an in-flight click
 * (which reads as "this nav item needs a double-click"). The RouteGuard still
 * enforces the real grant on click, so an item the user ultimately can't reach
 * shows an "Access restricted" panel rather than silently working. Once loaded,
 * the real gate applies (default-deny on an absent grant is unchanged).
 */
export function isNavItemVisible(
  gate: NavGate | undefined,
  perms: PermMap,
  role: string | undefined,
  isLoading = false,
): boolean {
  if (!gate) return true;
  if (isLoading) return true; // optimistic while the grid hydrates (guard enforces on click)
  if (gate.superAdmin) return role === 'super_admin';
  if (gate.entity) {
    // super_admin always holds every entity (reflection returns all-true), but
    // guard explicitly so a stale/partial grid can't hide a firm-owner surface.
    if (role === 'super_admin') return true;
    const action: PermissionAction = gate.action ?? 'can_view';
    return perms?.[gate.entity]?.[action] === true; // undefined → false (default-deny)
  }
  return true;
}

/**
 * Filter gated sections to the visible SidebarSection[] for the Sidebar.
 * Sections whose items all filter out are dropped entirely (no empty headers).
 */
export function visibleSections(
  sections: GatedSection[],
  perms: PermMap,
  role: string | undefined,
  isLoading = false,
): SidebarSection[] {
  const out: SidebarSection[] = [];
  for (const section of sections) {
    const items: SidebarItem[] = [];
    for (const item of section.items) {
      if (isNavItemVisible(item.gate, perms, role, isLoading)) {
        const { gate: _gate, ...plain } = item;
        void _gate;
        items.push(plain);
      }
    }
    if (items.length > 0) out.push({ label: section.label, items });
  }
  return out;
}
